import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import ConfirmModal from '../../../UI/Modal/confirmModal';
import ChecklistModal from '../../Settings/ChecklistModal';
import { ShotLightbox } from '../Screenshots';
import { structureOf } from '../../epmHelpers';
import { useEPMContext } from '../../../../contexts/EPMContext';
import { useUserContext } from '../../../../contexts/UserContext';
import { permission } from '../../../../config/permission';
import { deleteEpmChecklist } from '../../../../utils/APIs';
import { buildOf } from '../stepData';
import { notifyAutoSaved } from '../useAutoSave';
import { SIGN_OFF_ID } from './utils';
import VariantsSection from './VariantsSection';
import StagingChecklist from './StagingChecklist';
import ProductionChecklist from './ProductionChecklist';
import '../steps.css';

const BuildStep = ({ testData, setTestData }) => {

    const structure = structureOf(testData);
    const testId = testData?._id || testData?.id;

    const {
        checklists = [],
        stagingChecklist = [],
        productionChecklist = [],
        loading: contextLoading,
        updateTest,
        fetchTestChecklists,
        testChecklistsCache = {},
    } = useEPMContext();
    const { userData, checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);

    const cachedOneOff = testId ? testChecklistsCache[String(testId)] : undefined;
    const isOneOffCached = cachedOneOff !== undefined;
    const oneOffChecklists = cachedOneOff || [];
    const [fetchingOneOff, setFetchingOneOff] = useState(!isOneOffCached);
    const [showAddCheckModal, setShowAddCheckModal] = useState(false);
    const [deleteChecklistTarget, setDeleteChecklistTarget] = useState(null);
    const [isDeletingChecklist, setIsDeletingChecklist] = useState(false);
    const [shotsOpen, setShotsOpen] = useState(false);

    // Generic and test-specific checklists are guarded while loading
    const isChecklistsLoading = Boolean(contextLoading || (!isOneOffCached && fetchingOneOff));

    useEffect(() => {
        if (!testId || !fetchTestChecklists || isOneOffCached) return;
        setFetchingOneOff(true);
        fetchTestChecklists(testId)
            .catch((err) => console.error('Failed to load test one-off checklists:', err))
            .finally(() => setFetchingOneOff(false));
    }, [testId, isOneOffCached, fetchTestChecklists]);

    const refreshOneOffChecklists = useCallback(async () => {
        if (!testId || !fetchTestChecklists) return;
        setFetchingOneOff(true);
        try {
            await fetchTestChecklists(testId, true);
        } catch (err) {
            console.error('Failed to refresh test one-off checklists:', err);
        } finally {
            setFetchingOneOff(false);
        }
    }, [testId, fetchTestChecklists]);

    const oneOffProductionItems = useMemo(() => {
        return (oneOffChecklists || [])
            .filter((c) => (c.env || '').toUpperCase() === 'PRODUCTION')
            .map((c) => ({
                id: String(c._id || c.id),
                _id: String(c._id || c.id),
                label: c.name,
                detail: c.description,
                required: c.isRequired !== undefined ? c.isRequired : true,
                added: true,
                isOneOff: true,
                ...c,
            }));
    }, [oneOffChecklists]);

    const combinedProdList = useMemo(
        () => [...productionChecklist, ...oneOffProductionItems],
        [productionChecklist, oneOffProductionItems]
    );

    const build = useMemo(
        () => buildOf(testData, { stagingChecklist, productionChecklist: combinedProdList }),
        [testData, stagingChecklist, combinedProdList]
    );
    const mockups = structure.mockups;
    const hasShots = !!(mockups?.control?.src && mockups?.variant?.src);
    const expRef = build?.exp?.id || build?.exp?.name;

    const currentUserId = userData?.user_id || userData?.sub;
    const currentUserName = userData?.name || userData?.nickname || userData?.email;

    const stagingItems = useMemo(() => [...stagingChecklist, ...(build.added || [])], [stagingChecklist, build]);
    const productionChecks = useMemo(
        () => combinedProdList.filter((item) => item.id !== SIGN_OFF_ID && item.key !== SIGN_OFF_ID),
        [combinedProdList]
    );
    const signOffItem = useMemo(
        () =>
            productionChecklist.find((item) => item.id === SIGN_OFF_ID || item.key === SIGN_OFF_ID) || {
                id: SIGN_OFF_ID,
                label: 'Production sign-off recorded',
                required: true,
            },
        [productionChecklist]
    );

    const stagingSigned = !!build.staging.signed_by;
    const signedFromData = !!build.production.signed_by;
    const isSigned = signedFromData;
    const productionLocked = false; // Phase 1 decision: Drop lock, allow ticking production checks without staging sign-off

    const checklistLookup = useMemo(() => {
        const map = {};
        (checklists || []).forEach((c) => {
            const key = String(c._id || c.id || '');
            if (key) map[key] = { label: c.name, env: (c.env || 'STAGING').toUpperCase(), ...c };
        });
        (stagingChecklist || []).forEach((c) => {
            const key = String(c._id || c.id || '');
            if (key) map[key] = { ...map[key], ...c, env: 'STAGING' };
        });
        (productionChecklist || []).forEach((c) => {
            const key = String(c._id || c.id || '');
            if (key) map[key] = { ...map[key], ...c, env: 'PRODUCTION' };
        });
        (oneOffChecklists || []).forEach((c) => {
            const key = String(c._id || c.id || '');
            if (key) map[key] = { label: c.name, env: (c.env || 'PRODUCTION').toUpperCase(), ...c };
        });
        (build.added || []).forEach((c) => {
            const key = String(c._id || c.id || '');
            if (key) map[key] = { ...map[key], ...c, env: 'STAGING' };
        });
        return map;
    }, [checklists, stagingChecklist, productionChecklist, oneOffChecklists, build.added]);

    const allAudits = useMemo(() => {
        const audits = testData?.build_qa?.checklist_audit;
        if (!Array.isArray(audits)) return [];
        return [...audits].sort((a, b) => new Date(b.date_time || 0) - new Date(a.date_time || 0));
    }, [testData?.build_qa?.checklist_audit]);

    const stagingAudits = useMemo(() => {
        return allAudits.filter((entry) => {
            const item = checklistLookup[String(entry.checklist_id)];
            if (item?.env) return item.env.toUpperCase() === 'STAGING';
            return !String(entry.checklist_id || '').startsWith('prod');
        });
    }, [allAudits, checklistLookup]);

    const productionAudits = useMemo(() => {
        return allAudits.filter((entry) => {
            const item = checklistLookup[String(entry.checklist_id)];
            if (item?.env) return item.env.toUpperCase() === 'PRODUCTION';
            return String(entry.checklist_id || '').startsWith('prod');
        });
    }, [allAudits, checklistLookup]);

    // Track checked checklist IDs strictly from testData.build_qa.checklist
    const [checkedIds, setCheckedIds] = useState(() => {
        return (testData?.build_qa?.checklist || []).map(String);
    });

    useEffect(() => {
        setCheckedIds((testData?.build_qa?.checklist || []).map(String));
    }, [testData?.build_qa?.checklist]);

    const isStagingChecked = (item, index) => {
        const id = String(item?._id || item?.id || (index !== undefined ? `staging-${index}` : ''));
        return id ? checkedIds.includes(id) : false;
    };

    const isProductionChecked = (item, index) => {
        const id = String(item?._id || item?.id || (index !== undefined ? `prod-${index}` : ''));
        return id ? checkedIds.includes(id) : false;
    };

    const handleToggleChecklist = async (itemId) => {
        if (!canUpdate || !itemId) return;
        const strId = String(itemId);
        const nextIds = checkedIds.includes(strId)
            ? checkedIds.filter((id) => id !== strId)
            : [...checkedIds, strId];

        setCheckedIds(nextIds);

        if (testId && updateTest) {
            try {
                const res = await updateTest(testId, {
                    build_qa: { checklist: nextIds },
                });
                if (res && setTestData) {
                    setTestData((prev) => ({
                        ...prev,
                        ...res,
                        build_qa: {
                            ...prev?.build_qa,
                            ...res?.build_qa,
                        },
                    }));
                }
                notifyAutoSaved();
            } catch (err) {
                console.error('Failed to update test checklist:', err);
            }
        }
    };

    const toggleStaging = (item, index) => {
        if (stagingSigned) return;
        const id = item._id || item.id || `staging-${index}`;
        handleToggleChecklist(id);
    };

    const toggleCheck = (item, index) => {
        if (productionLocked) return;
        const id = item._id || item.id || `prod-${index}`;
        handleToggleChecklist(id);
    };

    const handleDeleteOneOffChecklist = async () => {
        if (!canUpdate || !deleteChecklistTarget?._id) return;
        setIsDeletingChecklist(true);
        try {
            const targetId = String(deleteChecklistTarget._id);
            await deleteEpmChecklist(userData?.token, userData?.selectedOrganization?.org_token, targetId);

            if (checkedIds.includes(targetId)) {
                const nextIds = checkedIds.filter((id) => id !== targetId);
                setCheckedIds(nextIds);
                if (testId && updateTest) {
                    const res = await updateTest(testId, {
                        build_qa: { checklist: nextIds },
                    });
                    if (res && setTestData) {
                        setTestData((prev) => ({
                            ...prev,
                            ...res,
                            build_qa: {
                                ...prev?.build_qa,
                                ...res?.build_qa,
                            },
                        }));
                    }
                }
            }

            toast.success('One-off check deleted successfully.');
            setDeleteChecklistTarget(null);
            await refreshOneOffChecklists();
        } catch (err) {
            console.error('Failed to delete one-off check:', err);
            toast.error(err?.message || "Couldn't delete the one-off check.");
        } finally {
            setIsDeletingChecklist(false);
        }
    };

    return (
        <div className="epm-plan epm-build">
            <header className="epm-workflow-step__heading epm-plan__heading">
                <span>04</span>
                <div>
                    <h2>Build & QA</h2>
                    <p>Build the variants, prove them in staging and production, and record a real sign-off before any traffic is touched.</p>
                </div>
            </header>

            <div className="flex flex-col gap-6">
                <VariantsSection
                    testData={testData}
                    setTestData={setTestData}
                    testId={testId}
                    mockups={mockups}
                    hasShots={hasShots}
                    updateTest={updateTest}
                    userData={userData}
                />

                <StagingChecklist
                    stagingItems={stagingItems}
                    stagingSigned={stagingSigned}
                    isChecklistsLoading={isChecklistsLoading}
                    isStagingChecked={isStagingChecked}
                    toggleStaging={toggleStaging}
                    canUpdate={canUpdate}
                    boundDay={build?.bound_day}
                    stagingAudits={stagingAudits}
                    checklistLookup={checklistLookup}
                    currentUserId={currentUserId}
                    currentUserName={currentUserName}
                />

                <ProductionChecklist
                    productionChecks={productionChecks}
                    signOffItem={signOffItem}
                    isSigned={isSigned}
                    productionLocked={productionLocked}
                    isChecklistsLoading={isChecklistsLoading}
                    isProductionChecked={isProductionChecked}
                    toggleCheck={toggleCheck}
                    canUpdate={canUpdate}
                    setDeleteChecklistTarget={setDeleteChecklistTarget}
                    onOpenAddCheckModal={() => setShowAddCheckModal(true)}
                    build={build}
                    boundDay={build?.bound_day}
                    expRef={expRef}
                    productionAudits={productionAudits}
                    checklistLookup={checklistLookup}
                    currentUserId={currentUserId}
                    currentUserName={currentUserName}
                />

                <div className="flex flex-col gap-4">
                </div>
            </div>

            {shotsOpen && hasShots && <ShotLightbox mockups={mockups} onClose={() => setShotsOpen(false)} />}

            {deleteChecklistTarget && (
                <ConfirmModal
                    title="Delete One-off Check"
                    description={`Are you sure you want to delete "${deleteChecklistTarget.name || deleteChecklistTarget.label}" from this test?`}
                    ConfirmText="Delete"
                    loading={isDeletingChecklist}
                    hideModal={() => setDeleteChecklistTarget(null)}
                    handleConfirmation={handleDeleteOneOffChecklist}
                />
            )}

            {showAddCheckModal && (
                <ChecklistModal
                    defaultEnv="PRODUCTION"
                    lockEnv={true}
                    testId={testId}
                    onClose={() => setShowAddCheckModal(false)}
                    onSaved={async () => {
                        setShowAddCheckModal(false);
                        await refreshOneOffChecklists();
                    }}
                />
            )}
        </div>
    );
};

export default BuildStep;

